import API_BASE from '../config';
import React, { useState } from 'react';
import { Loader2, Link2, Share2, Check, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const API_URL = API_BASE;

// Brand colors
const colors = {
  primary: '#7c3aed',
  primaryHover: '#6d28d9',
  primaryLight: '#F3EEFF',
  success: '#16A34A',
  successLight: '#D1FAE5',
  danger: '#DC2626',
  dangerLight: '#FEE2E2',
  navy: '#0D1F3C',
  body: '#6B7280',
  border: '#E5E9F0',
  surface: '#FFFFFF',
  muted: '#F3F4F6'
};

// Loading steps configuration
const LOADING_STEPS = [
  { duration: 1000, message: 'Connecting to LinkedIn...' },
  { duration: 2000, message: 'Fetching your work history...' },
  { duration: 2000, message: 'Preparing your profile...' },
  { duration: 60000, message: 'Almost there — this can take a few extra seconds...' },
];

const LinkedInImport = ({ onProfileImported, onProfileDataChange, onFallbackToPdf, sessionId, userEmail, whatsappNumber }) => {
  // State
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Validate LinkedIn URL in real-time
  const isValidUrl = (url) => {
    if (!url) return false;
    const cleanUrl = url.toLowerCase().trim();
    return cleanUrl.includes('linkedin.com/in/');
  };

  const urlIsValid = isValidUrl(linkedinUrl);

  // Animated loading steps
  const runLoadingAnimation = async () => {
    for (let i = 0; i < LOADING_STEPS.length; i++) {
      setLoadingStep(i);
      setProgressMessage(LOADING_STEPS[i].message);
      await new Promise(resolve => setTimeout(resolve, LOADING_STEPS[i].duration));
    }
  };

  // Main import handler
  const handleImport = async () => {
    if (!linkedinUrl.trim()) {
      setError('Please enter a LinkedIn profile URL');
      return;
    }

    if (!urlIsValid) {
      setError('Please enter a valid LinkedIn profile URL. Example: linkedin.com/in/yourname');
      return;
    }

    setError('');
    setIsImporting(true);
    setImportSuccess(false);
    setProfileData(null);

    // Run animation in parallel — do NOT await it; result shows as soon as API responds
    runLoadingAnimation();

    // Two-phase fetch (mobile-safe):
    //   1. POST /api/linkedin/fetch  → returns instantly. Either cached profile,
    //      or 200 {processing:true} meaning a background scrape was kicked off.
    //   2. If processing → poll GET /api/linkedin/cached?url=... every 2 s
    //      until the row appears (max ~90 s).
    // No single fetch ever lasts more than ~5 s, so mobile networks don't drop it.
    const fastFetch = async (input, init) => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 15000);
      try {
        const res = await fetch(input, { ...(init || {}), signal: ctrl.signal });
        return res;
      } finally {
        clearTimeout(t);
      }
    };

    const pollCached = async () => {
      // Poll every 2 s for up to 90 s. The background scrape takes ~24 s.
      const deadline = Date.now() + 90000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const res = await fastFetch(`${API_URL}/api/linkedin/cached?url=${encodeURIComponent(linkedinUrl)}`);
          if (res.ok) {
            const json = await res.json();
            if (json && json.profile) return json.profile;
          }
        } catch (_) {
          // transient — keep polling
        }
      }
      return null;
    };

    try {
      // ─── Step 1: kick off (with one retry on transient network failure) ───
      let data;
      try {
        const r1 = await fastFetch(`${API_URL}/api/linkedin/fetch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            linkedin_url: linkedinUrl,
            user_id: sessionId || null,
            user_email: userEmail || null,
            whatsapp_number: whatsappNumber || null,
            consent_given: true,
          }),
        });
        data = await r1.json();
      } catch (e1) {
        if (e1.name === 'TypeError' || e1.name === 'AbortError') {
          const r2 = await fastFetch(`${API_URL}/api/linkedin/fetch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              linkedin_url: linkedinUrl,
              user_id: sessionId || null,
              user_email: userEmail || null,
              whatsapp_number: whatsappNumber || null,
              consent_given: true,
            }),
          });
          data = await r2.json();
        } else {
          throw e1;
        }
      }

      if (!data.success && !data.processing) {
        throw new Error(data.error || 'Failed to import profile');
      }

      // ─── Step 2: cache hit returns immediately ───
      let profile = data.profile;
      let profileId = data.profile_id;

      // ─── Step 2b: cache miss → background scrape running, poll until done ───
      if (!profile && data.processing) {
        const polled = await pollCached();
        if (!polled) {
          throw new Error("This is taking longer than usual. Tap Try Again — your data is being prepared in the background.");
        }
        profile = polled;
        profileId = polled.profileId || null;
      }

      if (!profile) throw new Error('No profile data returned');

      // Structural scrape failure: the import returned essentially nothing.
      // Narrow on purpose — only fires when name/headline AND all experiences
      // are missing, i.e. the scrape failed, NOT when a real profile is simply
      // sparse (a junior profile with a name and one role must still evaluate).
      const noExperiences = !profile.experiences || profile.experiences.length === 0;
      const noIdentity = !profile.headline && !profile.full_name;
      if (noExperiences && noIdentity) {
        setError("We couldn't pull your full profile from LinkedIn this time. For the most accurate evaluation, upload your LinkedIn PDF instead.");
        setIsImporting(false);
        return;
      }

      setProfileData(profile);
      setProfileId(profileId);
      setImportSuccess(true);
      if (onProfileImported) onProfileImported(profile, profileId);

    } catch (err) {
      const msg = err.name === 'AbortError'
        ? 'Connection timed out. Tap "Try Again" — your data may already be on its way.'
        : err.name === 'TypeError'
          ? 'Couldn\'t reach our server. Check your internet and tap "Try Again".'
          : err.message;
      setError(msg);
    } finally {
      setIsImporting(false);
      setLoadingStep(0);
    }
  };

  // Handle experience edit
  // Reset and try again
  const handleReset = () => {
    setLinkedinUrl('');
    setError('');
    setProfileData(null);
    setProfileId(null);
    setImportSuccess(false);
  };

  // STATE 1 & 2: Initial / URL Input
  if (!importSuccess) {
    return (
      <div data-testid="linkedin-import-container">
        {/* URL Input */}
        <div className="space-y-3">
          <div>
            <label 
              className="block text-sm font-medium mb-1"
              style={{ color: colors.navy }}
            >
              Your LinkedIn URL
            </label>
            <div className="relative">
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => {
                  setLinkedinUrl(e.target.value);
                  setError('');
                }}
                placeholder="https://www.linkedin.com/in/yourname/"
                className="w-full px-4 py-3 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: error ? colors.danger : (urlIsValid && linkedinUrl ? colors.success : colors.border),
                  color: colors.navy
                }}
                disabled={isImporting}
                data-testid="linkedin-url-input"
              />
              {/* Validation indicator */}
              {linkedinUrl && !error && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {urlIsValid ? (
                    <Check className="w-5 h-5" style={{ color: colors.success }} />
                  ) : (
                    <AlertCircle className="w-5 h-5" style={{ color: colors.danger }} />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Find-it help: URL — collapsed by default so the Import button
              sits directly under the input on mobile. */}
          <div>
            <button
              type="button"
              onClick={() => setShowHelp(v => !v)}
              className="text-xs underline"
              style={{ color: colors.body }}
            >
              Where do I find this?
            </button>
            {showHelp && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-600">
                  How to find your LinkedIn URL
                </p>
                <div className="flex items-start gap-2">
                  <Link2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500">
                    <span className="font-medium text-slate-600">On desktop:</span> open your profile and copy the link from your browser's address bar.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Share2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500">
                    <span className="font-medium text-slate-600">On mobile:</span> open your profile, tap <span className="font-medium text-slate-600">⋯ → Share → Copy link to profile</span>.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="p-4 rounded-lg flex items-start gap-3"
              style={{ backgroundColor: colors.dangerLight }}
              data-testid="error-message"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.danger }} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: colors.danger }}>
                  {error}
                </p>
                <div className="flex gap-3 mt-3">
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                    style={{
                      backgroundColor: colors.surface,
                      color: colors.danger,
                      border: `1px solid ${colors.danger}`
                    }}
                    data-testid="try-again-btn"
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (onFallbackToPdf) onFallbackToPdf();
                    }}
                    className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                    style={{
                      backgroundColor: colors.primary,
                      color: '#fff'
                    }}
                    data-testid="upload-pdf-btn"
                  >
                    Upload PDF Instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isImporting && (
            <div 
              className="p-6 rounded-lg text-center"
              style={{ backgroundColor: colors.muted }}
              data-testid="loading-state"
            >
              <Loader2 
                className="w-10 h-10 mx-auto mb-4 animate-spin"
                style={{ color: colors.primary }}
              />
              <p className="text-sm font-medium" style={{ color: colors.navy }}>
                {progressMessage || 'Connecting to LinkedIn...'}
              </p>
              {/* Progress dots */}
              <div className="flex justify-center gap-2 mt-4">
                {LOADING_STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: idx <= loadingStep ? colors.primary : colors.border,
                      transform: idx === loadingStep ? 'scale(1.25)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Import Button */}
          {!isImporting && (
            <button
              type="button"
              onClick={handleImport}
              disabled={!urlIsValid || isImporting}
              className="w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: urlIsValid ? colors.primary : colors.muted,
                color: urlIsValid ? '#fff' : colors.body,
                cursor: urlIsValid ? 'pointer' : 'not-allowed'
              }}
              data-testid="import-btn"
            >
              <Link2 className="w-4 h-4" />
              Import Profile
            </button>
          )}
        </div>
      </div>
    );
  }

  // STATE 4: Success — Step 12 A2: success message only, no career preview.
  // Same pattern as the free-eval flow.
  return (
    <div className="space-y-4" data-testid="import-success-container">
      <div
        className="p-4 rounded-lg flex items-center gap-3"
        style={{ backgroundColor: colors.successLight }}
      >
        <CheckCircle className="w-5 h-5" style={{ color: colors.success }} />
        <p className="text-sm font-medium" style={{ color: colors.success }}>
          ✓ LinkedIn profile pulled successfully
        </p>
      </div>
      <button
        type="button"
        onClick={handleReset}
        className="w-full py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
        style={{ color: colors.body }}
        data-testid="reset-btn"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Import a different profile
      </button>
    </div>
  );
};

export default LinkedInImport;
